// ============================================================
// ProjectCommand · /square/projects/:id/command
//
// The single per-project command surface. It REPLACES the retired
// per-project operator dashboard and collapses the scattered
// project pages into one honest command deck: files, tasks,
// workflows, access, comms, creative, team, security, portal, and
// meetings.
//
// Phase-1 truth boundary: this file is real chrome + honest tabs
// with ZERO fabricated data. No project-command endpoint exists
// yet, so no such endpoint is fetched. The Overview tab links only
// to sub-routes that are actually built (Studio / Site / Hosting /
// Client Access) and reads the project id + status from the real
// useProject(id) hook. Every other tab renders an honest
// "not-yet-built" placeholder — never invented rows.
//
// ProjectSurface owns the loading / not-found chrome and the
// project identity header, so this component stays focused on the
// tab deck and its honest panels.
// ============================================================

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Tabs } from '@square/ui'
// Imported per the operator-surface contract. This Phase-1 shell
// intentionally makes NO admin-gated command fetch (no such route
// is built), so these guards are not exercised here — they are the
// standard surface's honest-error vocabulary and will gate the real
// fetches when the project-command endpoints land.
import { ApiError, isAuthError, isForbiddenError } from '../../api/client'
import { useProject } from '../../hooks/useProjects'
import { SHELL_TOKENS, chipStyle, text } from '../../shell/styles'
import ProjectSurface from './ProjectSurface'

// Reference the imported guards once so the operator-surface
// contract is documented in-file without a fetch to exercise them.
// Zero runtime cost; keeps the honest-error vocabulary discoverable
// for the phase that wires the real endpoints.
void ApiError
void isAuthError
void isForbiddenError

// ------------------------------------------------------------
// Tab registry — the 12 command modules, in contract order.
// ------------------------------------------------------------

type TabId =
  | 'overview'
  | 'vault'
  | 'documents'
  | 'tasks'
  | 'workflows'
  | 'alerts'
  | 'communications'
  | 'creative'
  | 'team'
  | 'security'
  | 'portal'
  | 'meetings'

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'vault', label: 'Vault' },
  { id: 'documents', label: 'Documents' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'workflows', label: 'Workflows' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'communications', label: 'Communications' },
  { id: 'creative', label: 'Creative' },
  { id: 'team', label: 'Team' },
  { id: 'security', label: 'Security' },
  { id: 'portal', label: 'Portal' },
  { id: 'meetings', label: 'Meetings' },
]

// Honest module copy — one line per placeholder tab. No tab claims
// data it does not have.
const PLACEHOLDER_COPY: Record<Exclude<TabId, 'overview'>, string> = {
  vault:
    'The project vault (scoped secrets + access codes) lands in a later phase — no data is fabricated.',
  documents:
    'Project documents and deliverables land in a later phase — no data is fabricated.',
  tasks:
    'Project tasks and their board land in a later phase — no data is fabricated.',
  workflows:
    'Per-project workflow runs land in a later phase — no data is fabricated.',
  alerts:
    'Project alerts and their triage land in a later phase — no data is fabricated.',
  communications:
    'Project communications threads land in a later phase — no data is fabricated.',
  creative:
    'The creative / content lane for this project lands in a later phase — no data is fabricated.',
  team:
    'The project team roster lands in a later phase — no data is fabricated.',
  security:
    'The project security posture lands in a later phase — no data is fabricated.',
  portal:
    'The member Secure Portal lane for this project lands in a later phase — no data is fabricated.',
  meetings:
    'Project meetings and controlled calls land in a later phase — no data is fabricated.',
}

export default function ProjectCommand() {
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState<TabId>('overview')

  return (
    <ProjectSurface
      kicker="PROJECT · COMMAND"
      title="Project Command"
      description="One command surface for this project — files, tasks, workflows, access, comms, creative, team, security, portal, and meetings."
    >
      <Tabs
        items={TABS}
        activeId={activeTab}
        onChange={(next: string) => setActiveTab(next as TabId)}
      />

      <div style={{ marginTop: 20 }}>
        {activeTab === 'overview' ? (
          <OverviewPanel projectId={id} />
        ) : (
          <PlaceholderPanel copy={PLACEHOLDER_COPY[activeTab]} />
        )}
      </div>
    </ProjectSurface>
  )
}

// ------------------------------------------------------------
// Overview — the only tab with real content. Shows the project id
// + live status and links to the sub-routes that actually exist.
// ------------------------------------------------------------

function OverviewPanel({ projectId }: { projectId: string | undefined }) {
  const navigate = useNavigate()
  const { project, loading } = useProject(projectId)

  // Sub-routes that are genuinely wired today. Everything else in
  // the tab deck is an honest placeholder above.
  const links: { label: string; hint: string; to: string }[] = projectId
    ? [
        {
          label: 'Studio',
          hint: 'Content Studio for this project',
          to: `/square/projects/${projectId}/studio`,
        },
        {
          label: 'Site',
          hint: 'Site Builder + pages',
          to: `/square/projects/${projectId}/site`,
        },
        {
          label: 'Hosting',
          hint: 'Deployments + DNS',
          to: `/square/projects/${projectId}/hosting`,
        },
        {
          label: 'Client Access',
          hint: 'Access control panel',
          to: `/square/projects/${projectId}/client-access`,
        },
      ]
    : []

  const status = project?.status
  const statusAccentColor = SHELL_TOKENS.green

  return (
    <div style={panelStyle}>
      <div style={{ ...text.kicker, marginBottom: 12 }}>Overview</div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 6,
        }}
      >
        <span style={metaLabelStyle}>Project ID</span>
        <span style={{ ...text.code, color: SHELL_TOKENS.ink }}>
          {projectId || '—'}
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 18,
        }}
      >
        <span style={metaLabelStyle}>Status</span>
        {loading ? (
          <span style={{ ...text.rowMeta }}>Loading…</span>
        ) : status ? (
          <span style={chipStyle(statusAccentColor)}>{status}</span>
        ) : (
          <span style={{ ...text.rowMeta }}>Unknown</span>
        )}
      </div>

      <div
        style={{
          fontSize: 12,
          color: SHELL_TOKENS.inkMuted,
          fontFamily: 'var(--font-mono)',
          marginBottom: 14,
        }}
      >
        Jump to a wired sub-surface for this project. Other command
        modules land in a later phase and are shown as honest
        placeholders — no data is fabricated.
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 10,
        }}
      >
        {links.map((link) => (
          <button
            key={link.to}
            type="button"
            onClick={() => navigate(link.to)}
            style={linkCardStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = SHELL_TOKENS.brandLine
              e.currentTarget.style.background = SHELL_TOKENS.brandSoft
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = SHELL_TOKENS.border
              e.currentTarget.style.background = SHELL_TOKENS.surface
            }}
          >
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: SHELL_TOKENS.ink,
                fontFamily: 'var(--font-mono)',
              }}
            >
              {link.label}
            </span>
            <span
              style={{
                fontSize: 11,
                color: SHELL_TOKENS.inkFaint,
                fontFamily: 'var(--font-mono)',
              }}
            >
              {link.hint}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ------------------------------------------------------------
// Placeholder — honest "not built yet" panel. Gold "Requires
// backend setup" chip + muted "Coming in a later phase" chip.
// ------------------------------------------------------------

function PlaceholderPanel({ copy }: { copy: string }) {
  return (
    <div style={panelStyle}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={chipStyle(SHELL_TOKENS.gold)}>Requires backend setup</span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '3px 9px',
            borderRadius: 6,
            background: SHELL_TOKENS.surfaceAlt,
            border: `1px solid ${SHELL_TOKENS.border}`,
            color: SHELL_TOKENS.inkFaint,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)',
          }}
        >
          Coming in a later phase
        </span>
      </div>
      <div
        style={{
          fontSize: 13,
          color: SHELL_TOKENS.inkDim,
          lineHeight: 1.6,
          fontFamily: 'var(--font-mono)',
        }}
      >
        {copy}
      </div>
    </div>
  )
}

// ------------------------------------------------------------
// Shared inline style objects (tokens only).
// ------------------------------------------------------------

const panelStyle = {
  background: SHELL_TOKENS.surface,
  border: `1px solid ${SHELL_TOKENS.border}`,
  borderRadius: 12,
  padding: 20,
} as const

const metaLabelStyle = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.15em',
  textTransform: 'uppercase' as const,
  color: SHELL_TOKENS.inkFaint,
  fontFamily: 'var(--font-mono)',
}

const linkCardStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 4,
  alignItems: 'flex-start',
  textAlign: 'left' as const,
  padding: '12px 14px',
  borderRadius: 10,
  border: `1px solid ${SHELL_TOKENS.border}`,
  background: SHELL_TOKENS.surface,
  cursor: 'pointer',
  transition: 'border-color 0.15s, background 0.15s',
}
